/**
 * Scheduled Ride Booking Page
 * Uses shared AddressInput component (same as Package flow)
 * Isolated ride page - does not touch package system
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ArrowLeft, MapPin, Navigation, Car, Calendar, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import VehicleSelector from './components/VehicleSelector';
import AddressInput from '../../components/shared/AddressInput';
import RideFareEstimate from './components/RideFareEstimate';
import RideMapPreview from './components/RideMapPreview';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ScheduledRide = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  // Form state - using same structure as package flow
  const [pickup, setPickup] = useState({
    street: '',
    city: '',
    parish: 'Kingston',
    coordinates: null
  });
  const [dropoff, setDropoff] = useState({
    street: '',
    city: '',
    parish: 'Kingston',
    coordinates: null
  });
  const [vehicleType, setVehicleType] = useState('standard');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [notes, setNotes] = useState('');
  
  // API state
  const [estimate, setEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimateError, setEstimateError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(null);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (pickup?.coordinates && dropoff?.coordinates) {
      fetchEstimate();
    } else {
      setEstimate(null);
    }
  }, [pickup?.coordinates, dropoff?.coordinates, vehicleType]);

  const fetchEstimate = async () => {
    if (!pickup?.coordinates || !dropoff?.coordinates) return;
    
    setEstimateLoading(true);
    setEstimateError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/rides/estimate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          pickup: {
            address: pickup.fullAddress || `${pickup.street}, ${pickup.city}, ${pickup.parish}`,
            coordinates: pickup.coordinates,
            parish: pickup.parish
          },
          dropoff: {
            address: dropoff.fullAddress || `${dropoff.street}, ${dropoff.city}, ${dropoff.parish}`,
            coordinates: dropoff.coordinates,
            parish: dropoff.parish
          },
          vehicle_type: vehicleType
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setEstimate(data.estimate);
      } else {
        setEstimateError(data.error?.message || 'Failed to get estimate');
      }
    } catch (err) {
      setEstimateError('Network error. Please try again.');
    } finally {
      setEstimateLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!pickup?.coordinates || !dropoff?.coordinates) {
      setSubmitError('Please select pickup and destination with GPS coordinates');
      return;
    }

    if (!pickup.street || !dropoff.street) {
      setSubmitError('Please enter street addresses for pickup and destination');
      return;
    }

    if (!scheduledDate || !scheduledTime) {
      setSubmitError('Please select date and time for your ride');
      return;
    }

    const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
    
    if (scheduledDateTime <= new Date()) {
      setSubmitError('Please select a future date and time');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch(`${API_URL}/api/scheduled-rides`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          pickup: {
            address: pickup.fullAddress || `${pickup.street}, ${pickup.city}, ${pickup.parish}`,
            street: pickup.street,
            city: pickup.city,
            coordinates: pickup.coordinates,
            parish: pickup.parish
          },
          dropoff: {
            address: dropoff.fullAddress || `${dropoff.street}, ${dropoff.city}, ${dropoff.parish}`,
            street: dropoff.street,
            city: dropoff.city,
            coordinates: dropoff.coordinates,
            parish: dropoff.parish
          },
          vehicle_type: vehicleType,
          scheduled_time: scheduledDateTime.toISOString(),
          notes: notes || undefined,
          is_airport_pickup: false
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess({
          id: data.ride?.id || data.scheduled_ride?.id,
          scheduledTime: scheduledDateTime
        });
      } else {
        setSubmitError(data.error?.message || 'Failed to schedule ride');
      }
    } catch (err) {
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Ride Scheduled!</h2>
          <p className="text-gray-600 mb-4">Your ride has been scheduled for</p>
          <div className="bg-emerald-50 rounded-xl p-4 mb-6">
            <p className="text-lg font-semibold text-emerald-700">
              {success.scheduledTime.toLocaleDateString('en-JM', {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
              })}
            </p>
            <p className="text-2xl font-bold text-emerald-600">
              {success.scheduledTime.toLocaleTimeString('en-JM', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            We'll notify you when a driver is assigned and send reminders before pickup.
          </p>
          <div className="space-y-3">
            <Button onClick={() => navigate('/dashboard')} className="w-full bg-emerald-600 hover:bg-emerald-700">
              Back to Dashboard
            </Button>
            <Button
              onClick={() => {
                setSuccess(null);
                setPickup({ street: '', city: '', parish: 'Kingston', coordinates: null });
                setDropoff({ street: '', city: '', parish: 'Kingston', coordinates: null });
                setScheduledDate('');
                setScheduledTime('');
                setNotes('');
              }}
              variant="outline"
              className="w-full"
            >
              Schedule Another Ride
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="bg-blue-600 text-white p-4">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/ride/book')}
            className="p-2 hover:bg-blue-700 rounded-lg transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold">Schedule a Ride</h1>
            <p className="text-blue-100 text-sm">Book in advance</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-6 max-w-lg mx-auto">
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            When do you need a ride?
          </h3>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Date</label>
              <Input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={today}
                className="w-full"
                data-testid="schedule-date"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Time</label>
              <Input
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                className="w-full"
                data-testid="schedule-time"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
          <AddressInput
            label="Pickup Location"
            value={pickup}
            onChange={setPickup}
            placeholder="e.g., 3 Regal Ave Horizon Park Spanish Town"
            showGpsButton={true}
            theme="light"
            testIdPrefix="pickup"
          />
          
          <div className="border-l-2 border-dashed border-blue-300 h-4 ml-5" />
          
          <AddressInput
            label="Destination"
            value={dropoff}
            onChange={setDropoff}
            placeholder="e.g., 31 King Street Downtown Kingston"
            showGpsButton={true}
            theme="light"
            testIdPrefix="dropoff"
          />
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Car className="w-5 h-5 text-blue-600" />
            Choose Vehicle
          </h3>
          <VehicleSelector selected={vehicleType} onSelect={setVehicleType} disabled={estimateLoading} />
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4">
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Additional Notes (optional)
          </label>
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any special instructions for the driver?"
            className="w-full"
          />
        </div>

        <RideFareEstimate estimate={estimate} loading={estimateLoading} error={estimateError} />

        {/* Interactive Route Map */}
        <RideMapPreview
          pickup={pickup}
          dropoff={dropoff}
          onPickupDrag={(loc) => setPickup(prev => ({ ...prev, ...loc }))}
          onDropoffDrag={(loc) => setDropoff(prev => ({ ...prev, ...loc }))}
          height={300}
        />

        {submitError && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-700 font-medium">Scheduling Failed</p>
              <p className="text-red-600 text-sm">{submitError}</p>
            </div>
          </div>
        )}

        <Button
          onClick={handleSubmit}
          disabled={submitting || !pickup?.coordinates || !dropoff?.coordinates || !scheduledDate || !scheduledTime}
          className="w-full py-6 text-lg bg-blue-600 hover:bg-blue-700"
          data-testid="schedule-ride-btn"
        >
          {submitting ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
              Scheduling...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Schedule Ride
            </span>
          )}
        </Button>

        {(!pickup?.coordinates || !dropoff?.coordinates) && (pickup?.street || dropoff?.street) && (
          <p className="text-xs text-center text-gray-500">
            Type an address and click "Find GPS" or select from suggestions to set coordinates.
          </p>
        )}

        <div className="text-center text-sm text-gray-500">
          <p>You can cancel up to 30 minutes before pickup time for free.</p>
        </div>
      </div>
    </div>
  );
};

export default ScheduledRide;
